import {
  BuildingOfficeIcon,
  CalendarDateRangeIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { useSelector } from '@xstate/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { UserContext } from '~/app';
import { RichTextContent, Tabs } from '~/components';
import type { InquirySource } from '~/features/market/initialInquiry.machine';
import '~/features/market/listing-search/listingPresentation.css';
import { CompensationLayout } from '~/features/market/listing-search/sub-components/Compensation';
import { ListingContractTerms } from '~/features/market/listing-search/sub-components/ContractTerms';
import { CostCalculator } from '~/features/market/listings/job-listing/wizard-steps/cost-calculator/CostCalculator';
import { formatSubspecialties } from '~/features/market/listings/subspecialties';
import { ResponseNegotiationModal } from '~/features/market/negotiations/ResponseNegotiationModal';
import type { DraftSubmitResult } from '~/features/market/negotiations/types';
import { MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import type { MarketFact } from '~/features/shared/market-content';
import {
  formatStatusLabel,
  MarketActionRow,
  MarketContentPanel,
  MarketEmptyPanel,
  MarketFactGrid,
  MarketLoadingPanel,
  MarketPanelHeader,
  MarketSelectableCard,
  MarketStatusPill,
} from '~/features/shared/market-content';
import { InitialInquiryFlow } from '~/features/shared/market-content/InitialInquiryFlow';
import { selectUnmaskViewerScope } from '~/features/shared/market-content/ResponseUnmaskPanel';
import { SearchFit } from '~/features/shared/market-content/SearchFit';
import { useSearchFit } from '~/features/shared/market-content/useSearchFit';
import { ModalDialog } from '~/features/vault/documents/ModalDialog';
import { buildJobOpportunitiesUrl } from '~/routing/urls';
import type { EmployerFacility } from '~/types/hlx/employerFacility';
import type { JobListing } from '~/types/hlx/jobListing';
import type { JobListingDetail } from '~/types/hlx/jobListingDetail';
import type { JobResponse } from '~/types/hlx/jobResponse';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { getData } from '~/utils/service';
import { stripHtml } from '~/utils/stripHtml';
import { loadProviderJobSearchDefaults } from './providerJobSearchDefaults';
import { ProviderJobSearchFilters } from './ProviderJobSearchFilters';
import {
  EMPTY_PROVIDER_JOB_SEARCH_FILTERS,
  type ProviderJobSearchFilterValues,
  serializeProviderJobSearchFilters,
} from './providerJobSearchFilters.schema';
import { ProviderJobShiftDetails } from './ProviderJobShiftDetails';
import { useJobApplication } from './useJobApplication';

type ProviderBrowsableJobListing = Pick<
  JobListing,
  | 'id'
  | 'jobTitle'
  | 'jobDescription'
  | 'providerCategoryCode'
  | 'specialtyName'
  | 'subspecialties'
  | 'generalSpecialtyFlag'
  | 'jobType'
  | 'workLocation'
  | 'stateCodes'
> & {
  createdAt?: string;
  detail?: Partial<JobListingDetail> | null;
  listingOwner?: { displayName: string; masked: boolean };
  primaryFacility?:
    | (Pick<EmployerFacility, 'clinicalSetting' | 'facilitySize' | 'areaType'> & {
        facilityName: string | null;
        masked: boolean;
      })
    | null;
  jobResponses?: JobResponse[];
};

const ownerDisplayName = (listing: ProviderBrowsableJobListing) =>
  listing.listingOwner?.displayName || 'Identity masked';

const facilityDisplayName = (listing: ProviderBrowsableJobListing) => {
  if (!listing.primaryFacility) return 'Primary facility not listed';
  if (listing.listingOwner?.masked !== false || listing.primaryFacility.masked !== false) {
    return 'Facility identity masked';
  }
  return listing.primaryFacility.facilityName || 'Primary facility not listed';
};

const loadProviderJobListings = (filters: ProviderJobSearchFilterValues, signal: AbortSignal) =>
  withRequestDeadline(
    (requestSignal) =>
      getData<ProviderBrowsableJobListing[]>('jobListingMarketplaceSearch', {
        params: {
          page: '1',
          pageSize: '100',
          ...serializeProviderJobSearchFilters(filters),
        },
        signal: requestSignal,
      }),
    MARKET_READ_TIMEOUT_MS,
    signal
  );

const formatDate = (value?: string | null) => {
  if (!value) return '—';

  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    // Contract dates are calendar days; posted timestamps use the viewer's zone.
    ...(/^\d{4}-\d{2}-\d{2}$/.test(value) ? { timeZone: 'UTC' } : {}),
  });
};

const formatCompensation = (detail?: Partial<JobListingDetail> | null) => {
  const pricePerHour = detail?.pricePerHour;

  if (pricePerHour === null || pricePerHour === undefined) {
    return 'Compensation not listed';
  }

  const numericPrice = Number(pricePerHour);

  if (Number.isNaN(numericPrice)) {
    return 'Compensation not listed';
  }

  return `$${numericPrice.toLocaleString()}/hr`;
};

const ListingSummaryItem = ({ icon, text }: { icon: ReactNode; text: string }) => (
  <div className="flex min-w-0 items-start gap-2">
    <div className="mt-0.5 shrink-0 text-content-secondary">{icon}</div>
    <p className="text-sm font-normal leading-6 text-content-secondary [overflow-wrap:anywhere]">
      {text}
    </p>
  </div>
);

// [MCV-1955] Same three tabs as the B2B listing detail (DetailPanel), with matching presentation.
const DETAIL_TABS = [
  { name: 'Contract Overview', cyTag: 'provider-job-listing-tab-overview' },
  { name: 'Compensation', cyTag: 'provider-job-listing-tab-compensation' },
  { name: 'Contract Terms', cyTag: 'provider-job-listing-tab-contract-terms' },
] as const;

const matchesSearch = (listing: ProviderBrowsableJobListing, searchText: string) => {
  if (!searchText) {
    return true;
  }

  const searchableFields = [
    listing.jobTitle,
    listing.specialtyName,
    listing.providerCategoryCode,
    ownerDisplayName(listing),
    stripHtml(listing.jobDescription),
    listing.workLocation,
    listing.stateCodes.join(' '),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return searchableFields.includes(searchText.toLowerCase());
};

export const ProviderJobListings = () => {
  const viewerScopeKey = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  const navigate = useNavigate();
  return (
    <InitialInquiryFlow
      viewerScopeKey={viewerScopeKey}
      onStarted={(response) => navigate(buildJobOpportunitiesUrl({ responseId: response.id }))}
    >
      {(controls) => (
        <ProviderJobListingsContent
          key={viewerScopeKey}
          viewerScopeKey={viewerScopeKey}
          {...controls}
          onOpenConversation={(id) => navigate(buildJobOpportunitiesUrl({ responseId: id }))}
        />
      )}
    </InitialInquiryFlow>
  );
};

const ProviderJobListingsContent = ({
  viewerScopeKey,
  openInquiry,
  inquiryBusy,
  onOpenConversation,
}: {
  viewerScopeKey: string;
  openInquiry: (source: InquirySource) => void;
  inquiryBusy: boolean;
  onOpenConversation: (responseId: number) => void;
}) => {
  const [listings, setListings] = useState<ProviderBrowsableJobListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [negotiationResponseId, setNegotiationResponseId] = useState<number | null>(null);
  const [appliedListingId, setAppliedListingId] = useState<number | null>(null);
  const application = useJobApplication((response) => {
    setListings((current) =>
      current.map((listing) =>
        listing.id === response.jobListingId ? { ...listing, jobResponses: [response] } : listing
      )
    );
    setAppliedListingId(response.jobListingId ?? null);
  });
  const applying = application.busy;
  const [negotiationStartDraftIfEmpty, setNegotiationStartDraftIfEmpty] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedListingId, setSelectedListingId] = useState<number | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<ProviderJobSearchFilterValues>(
    EMPTY_PROVIDER_JOB_SEARCH_FILTERS
  );
  // [MCV-1822] The provider's own category/specialty, once known: they fill the filter form
  // and are the first search. Only the first load uses them; Clear All goes back to empty.
  const [initialFilters, setInitialFilters] = useState<ProviderJobSearchFilterValues | null>(null);

  // Latest-request-wins: every load increments this ref, and a resolved
  // request only applies its results/errors when the sequence still matches --
  // a slower, earlier search cannot clobber a newer Search or Clear All.
  const requestSeq = useRef(0);
  const searchController = useRef<AbortController | null>(null);

  const reconcileSelection = (data: ProviderBrowsableJobListing[]) => {
    setSelectedListingId((current) =>
      current != null && data.some((listing) => listing.id === current)
        ? current
        : (data[0]?.id ?? null)
    );
  };

  const runSearch = async (filters: ProviderJobSearchFilterValues) => {
    const seq = ++requestSeq.current;
    searchController.current?.abort();
    const controller = new AbortController();
    searchController.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await loadProviderJobListings(filters, controller.signal);
      if (requestSeq.current !== seq) {
        return;
      }
      setListings(data);
      reconcileSelection(data);
    } catch (fetchError) {
      if (requestSeq.current !== seq) {
        return;
      }
      setError(fetchError instanceof Error ? fetchError.message : 'Unable to load job listings');
    } finally {
      if (requestSeq.current === seq) {
        searchController.current = null;
        setLoading(false);
      }
    }
  };

  const runInitialSearch = async () => {
    const seq = ++requestSeq.current;
    let defaults: ProviderJobSearchFilterValues | null = null;
    try {
      defaults = await withRequestDeadline(
        (signal) => loadProviderJobSearchDefaults(signal),
        MARKET_READ_TIMEOUT_MS
      );
    } catch {
      // No defaults is the same as a provider with no category: search unfiltered.
    }
    if (requestSeq.current !== seq) {
      return;
    }
    if (defaults) {
      setAppliedFilters(defaults);
      setInitialFilters(defaults);
    }
    await runSearch(defaults ?? EMPTY_PROVIDER_JOB_SEARCH_FILTERS);
  };

  useEffect(() => {
    void runInitialSearch();
    return () => {
      ++requestSeq.current;
      searchController.current?.abort();
    };
    // Only on mount: subsequent loads are driven by Search/Clear All/refresh.
  }, []);

  const filteredListings = listings.filter((listing) => matchesSearch(listing, searchText));
  const fit = useSearchFit(
    'job',
    viewerScopeKey,
    loading ? [] : listings.map((listing) => listing.id),
    listings
  );
  const selectedListing =
    filteredListings.find((listing) => listing.id === selectedListingId) ??
    filteredListings[0] ??
    null;
  // [MCV-1955] The tab belongs to the listing it was picked on; another listing opens on the first.
  // Leaving a listing forgets its tab, so coming back to it (A -> B -> A) opens the overview too.
  const [detailTabState, setDetailTabState] = useState({ listingId: -1, index: 0 });
  const selectedListingKey = selectedListing?.id ?? -1;
  if (detailTabState.listingId !== selectedListingKey) {
    setDetailTabState({ listingId: selectedListingKey, index: 0 });
  }
  const detailTab = detailTabState.listingId === selectedListingKey ? detailTabState.index : 0;

  useEffect(() => {
    if (!selectedListing && filteredListings[0]) {
      setSelectedListingId(filteredListings[0].id);
    }
  }, [filteredListings, selectedListing]);

  const refreshListings = async () => {
    // Refreshing after a negotiation modal closes must re-apply the filters
    // currently in effect, never fall back to an unfiltered request.
    await runSearch(appliedFilters);
  };

  const handleFilterSearch = (values: ProviderJobSearchFilterValues) => {
    setAppliedFilters(values);
    void runSearch(values);
  };

  const handleClearAll = () => {
    setAppliedFilters(EMPTY_PROVIDER_JOB_SEARCH_FILTERS);
    setSearchText('');
    void runSearch(EMPTY_PROVIDER_JOB_SEARCH_FILTERS);
  };

  const selectedResponse = selectedListing?.jobResponses?.[0];
  const hasApplied = selectedResponse != null && selectedResponse.status !== 'MESSAGED';
  const negotiateLabel =
    selectedResponse?.latestNegotiationId != null ? 'Open Negotiation' : 'Negotiate';
  const selectedSubspecialties = selectedListing ? formatSubspecialties(selectedListing) : null;
  const selectedListingFacts: MarketFact[] = selectedListing
    ? [
        { label: 'Provider Type', value: selectedListing.providerCategoryCode || 'Not listed' },
        { label: 'Specialty', value: selectedListing.specialtyName || 'Not listed' },
        ...(selectedSubspecialties
          ? [
              {
                label: 'Subspecialties',
                value: selectedSubspecialties,
                cyTag: 'provider-job-detail-subspecialties',
              },
            ]
          : []),
        { label: 'Job Type', value: formatStatusLabel(selectedListing.jobType) },
        {
          label: 'Work Location',
          value: selectedListing.workLocation
            ? formatStatusLabel(selectedListing.workLocation)
            : '—',
        },
        {
          label: 'Covered States',
          value:
            selectedListing.stateCodes.length > 0 ? selectedListing.stateCodes.join(', ') : '—',
        },
        {
          label: 'Compensation',
          value: formatCompensation(selectedListing.detail),
          highlight: true,
        },
        {
          label: 'Contract Dates',
          value:
            selectedListing.detail?.startDate && selectedListing.detail?.endDate
              ? `${formatDate(selectedListing.detail.startDate)} - ${formatDate(selectedListing.detail.endDate)}`
              : selectedListing.detail?.startDate
                ? `${formatDate(selectedListing.detail.startDate)}+`
                : selectedListing.detail?.endDate
                  ? `Until ${formatDate(selectedListing.detail.endDate)}`
                  : '—',
        },
      ]
    : [];

  const openApplication = () => {
    if (!selectedListing || applying || application.uncertain || inquiryBusy) return;
    application.openApplication({ id: selectedListing.id, jobTitle: selectedListing.jobTitle });
  };

  // Applying creates the response; opening negotiation only reads its draft.
  // The first offer is still persisted by Submit Offer (MCV-1646).
  const handleNegotiate = () => {
    if (!hasApplied || !selectedResponse || inquiryBusy || applying || application.uncertain)
      return;
    setNegotiationResponseId(selectedResponse.id);
    setNegotiationStartDraftIfEmpty(selectedResponse.latestNegotiationId == null);
  };

  const handleDraftSubmitted = (result: DraftSubmitResult) => {
    setListings((currentListings) =>
      currentListings.map((listing) =>
        listing.jobResponses?.some((response) => response.id === result.jobResponseId)
          ? { ...listing, jobResponses: [result.jobResponse] }
          : listing
      )
    );
    // [MCV-1816] The popup closes after the first offer is sent (Details calls
    // onClose), so it is no longer re-opened on the new response here.
  };

  const handleNegotiationClose = () => {
    setNegotiationResponseId(null);
    void refreshListings();
  };

  const workspace = loading ? (
    <MarketLoadingPanel label="Loading job listings" />
  ) : error ? (
    <MarketContentPanel
      header={<MarketPanelHeader title="Job Listings" />}
      className="border-error/20"
    >
      <p className="text-[1rem] leading-6 text-error">{error}</p>
    </MarketContentPanel>
  ) : (
    <div
      className="flex min-h-[600px] min-w-0 flex-1 flex-col @min-[960px]/job-search:min-h-0"
      data-cy="provider-job-listings-workspace"
    >
      <div className="flex min-h-0 flex-1 flex-col @min-[960px]/job-search:flex-row">
        <aside
          aria-label="Job listings"
          className="max-h-[440px] w-full shrink-0 overflow-y-auto @min-[960px]/job-search:max-h-none @min-[960px]/job-search:w-[350px] @min-[1400px]/job-search:w-[420px]"
          data-cy="provider-job-listings-sidebar"
        >
          <div className="px-4 pt-4 text-left">
            <div className="mb-4 border-b border-grey-900 pb-3 text-left">
              <h2 className="mb-1 text-xl font-bold leading-snug text-content-primary">
                Job Listings
              </h2>
              <p className="text-sm font-normal leading-6 text-content-secondary">
                {filteredListings.length} {filteredListings.length === 1 ? 'listing' : 'listings'}{' '}
                found
              </p>
              <label className="mt-4 flex items-center gap-2 rounded-md border border-grey-900 px-3 py-2">
                <MagnifyingGlassIcon className="size-5 shrink-0 text-content-secondary" />
                <input
                  aria-label="Search job listings"
                  className="min-w-0 w-full bg-transparent text-[1rem] leading-6 outline-none placeholder:text-content-secondary"
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Search within results"
                  value={searchText}
                />
              </label>
            </div>

            {filteredListings.length === 0 ? (
              <MarketEmptyPanel
                title="No active job listings matched your search"
                className="min-h-[180px]"
              />
            ) : (
              filteredListings.map((listing) => {
                const isSelected = listing.id === selectedListing?.id;
                const stateSummary =
                  listing.stateCodes.length > 3
                    ? `${listing.stateCodes.length} states`
                    : listing.stateCodes.join(', ') || '—';
                const shiftSummary =
                  listing.detail?.hoursPerShift != null
                    ? `${listing.detail.hoursPerShift} hours/shift`
                    : 'Hours not listed';

                return (
                  <MarketSelectableCard
                    key={listing.id}
                    selected={isSelected}
                    dataCy={`provider-job-listing-${listing.id}`}
                    className="mb-3"
                    onClick={() => setSelectedListingId(listing.id)}
                  >
                    <h3 className="text-lg font-bold leading-snug text-content-primary [overflow-wrap:anywhere]">
                      {listing.jobTitle}
                    </h3>
                    <SearchFit
                      summary={fit.scores[listing.id]}
                      loading={fit.loading}
                      error={fit.error}
                    />
                    <div className="mt-2">
                      <MarketStatusPill
                        status="neutral"
                        size="md"
                        label={formatStatusLabel(listing.jobType)}
                      />
                    </div>
                    <p className="mb-2 mt-2 text-[1rem] font-normal leading-6 text-content-secondary [overflow-wrap:anywhere]">
                      {ownerDisplayName(listing)}
                    </p>
                    <dl className="mb-2 space-y-1 text-left">
                      <div className="flex items-baseline gap-2">
                        <dt className="shrink-0 whitespace-nowrap text-sm font-semibold leading-5 text-content-secondary">
                          Provider Type:
                        </dt>
                        <dd className="min-w-0 text-[1rem] font-normal leading-6 text-content-primary [overflow-wrap:anywhere]">
                          {listing.providerCategoryCode || 'Not listed'}
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="shrink-0 whitespace-nowrap text-sm font-semibold leading-5 text-content-secondary">
                          Specialty:
                        </dt>
                        <dd className="min-w-0 text-[1rem] font-normal leading-6 text-content-primary [overflow-wrap:anywhere]">
                          {listing.specialtyName || 'Not listed'}
                        </dd>
                      </div>
                    </dl>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                      <ListingSummaryItem
                        text={stateSummary}
                        icon={<MapPinIcon className="size-5" />}
                      />
                      <ListingSummaryItem
                        text={
                          listing.workLocation
                            ? formatStatusLabel(listing.workLocation)
                            : 'Location not listed'
                        }
                        icon={<BuildingOfficeIcon className="size-5" />}
                      />
                      <ListingSummaryItem
                        text={formatDate(listing.detail?.startDate)}
                        icon={<CalendarDateRangeIcon className="size-5" />}
                      />
                      <ListingSummaryItem
                        text={shiftSummary}
                        icon={<ClockIcon className="size-5" />}
                      />
                    </div>
                    <p className="mt-2 text-[1rem] font-semibold leading-6 text-primary-50">
                      {formatCompensation(listing.detail)}
                    </p>
                  </MarketSelectableCard>
                );
              })
            )}
          </div>
        </aside>

        <div
          aria-hidden="true"
          className="hidden w-1 shrink-0 bg-grey-900 @min-[960px]/job-search:block"
        />

        <section
          aria-label="Selected job listing"
          className="min-h-[600px] min-w-0 flex-1 overflow-y-auto px-0 pt-4 @min-[960px]/job-search:min-h-0 @min-[960px]/job-search:px-6"
          data-cy="provider-job-listings-detail-column"
        >
          {selectedListing ? (
            <>
              <MarketContentPanel
                className="b2b-listing-presentation"
                dataCy="provider-job-listing-detail"
              >
                <div className="text-left">
                  <h2 className="mb-4 text-2xl font-bold leading-snug text-content-primary [overflow-wrap:anywhere] @min-[1400px]/job-search:text-[28px]">
                    {selectedListing.jobTitle}
                  </h2>
                  <SearchFit
                    summary={fit.scores[selectedListing.id]}
                    loading={fit.loading}
                    error={fit.error}
                    onRetry={fit.retry}
                  />

                  <Tabs
                    tabs={DETAIL_TABS.map((tab, index) => ({
                      ...tab,
                      current: index === detailTab,
                    }))}
                    activeTab={detailTab}
                    handleTabChange={(index) =>
                      setDetailTabState({ listingId: selectedListing.id, index })
                    }
                  />

                  {detailTab === 0 && (
                    <div data-cy="provider-job-listing-overview">
                      <section
                        className="border-b border-grey-900 pb-5 text-left"
                        data-cy="provider-job-listing-description"
                      >
                        <h3 className="text-xl font-bold leading-snug text-content-primary">
                          Job Description
                        </h3>
                        <RichTextContent
                          value={selectedListing.jobDescription}
                          className="mt-3 text-[1rem] font-normal leading-relaxed text-content-secondary [overflow-wrap:anywhere] @min-[1400px]/job-search:text-lg"
                          fallback="No job description was provided for this listing."
                        />
                      </section>

                      <div
                        className="my-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm leading-6 text-content-secondary"
                        data-cy="provider-job-listing-metadata"
                      >
                        <MarketStatusPill
                          status="neutral"
                          size="md"
                          label={formatStatusLabel(selectedListing.jobType)}
                        />
                        <span className="[overflow-wrap:anywhere]">
                          {ownerDisplayName(selectedListing)}
                        </span>
                        {selectedListing.listingOwner?.masked !== false && (
                          <span>Identity masked</span>
                        )}
                        <span>Posted: {formatDate(selectedListing.createdAt)}</span>
                      </div>

                      <ProviderJobShiftDetails detail={selectedListing.detail} />

                      <section className="mt-6" aria-label="Job details">
                        <h3 className="mb-4 text-xl font-bold leading-snug text-content-primary">
                          Job Details
                        </h3>
                        <MarketFactGrid facts={selectedListingFacts} density="comfortable" />
                      </section>

                      <section
                        className="mt-6 rounded-lg bg-grey-925 p-5 text-left"
                        aria-label="Facility"
                      >
                        <h3 className="text-xl font-bold leading-snug text-content-primary">
                          Facility
                        </h3>
                        <p className="mt-3 text-[1rem] font-normal leading-6 text-content-primary [overflow-wrap:anywhere]">
                          {facilityDisplayName(selectedListing)}
                        </p>
                        <p className="mt-1 text-[1rem] font-normal leading-6 text-content-secondary [overflow-wrap:anywhere]">
                          {[
                            selectedListing.primaryFacility?.clinicalSetting,
                            selectedListing.primaryFacility?.facilitySize,
                            selectedListing.primaryFacility?.areaType,
                          ]
                            .filter(Boolean)
                            .join(' • ') || 'Facility details unavailable'}
                        </p>
                      </section>
                    </div>
                  )}
                  {detailTab === 1 && (
                    <CompensationLayout dataCy="provider-job-listing-compensation">
                      <CostCalculator
                        detail={selectedListing.detail}
                        loading={false}
                        showHeading={false}
                        failureAudience="provider"
                      />
                    </CompensationLayout>
                  )}
                  {detailTab === 2 && (
                    <ListingContractTerms
                      key={selectedListing.id}
                      dataCy="provider-job-listing-contract-terms"
                      detail={selectedListing.detail}
                    />
                  )}
                </div>
              </MarketContentPanel>

              <div className="sticky bottom-0 z-20 border-t border-grey-900 bg-white/90 shadow-xl backdrop-blur-sm">
                <div className="mx-auto flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <p role="status" className="text-sm leading-6 text-content-secondary">
                    {appliedListingId === selectedListing.id
                      ? 'Application submitted. You can now negotiate terms.'
                      : hasApplied
                        ? 'Application submitted'
                        : selectedResponse?.status === 'MESSAGED'
                          ? 'Inquiry sent. You can still apply for this job.'
                          : 'Message to ask a question, or Apply to express interest in this job.'}
                  </p>
                  <MarketActionRow
                    size="md"
                    actions={[
                      {
                        label: hasApplied ? negotiateLabel : 'Apply',
                        onClick: hasApplied ? handleNegotiate : openApplication,
                        colorVariant: hasApplied ? 'secondary' : 'primary',
                        disabled: inquiryBusy || applying || application.uncertain,
                      },
                      {
                        label: selectedResponse ? 'Open conversation' : 'Message',
                        onClick: () =>
                          selectedResponse
                            ? onOpenConversation(selectedResponse.id)
                            : openInquiry({
                                target: { listingType: 'JOB', jobListingId: selectedListing.id },
                                title: selectedListing.jobTitle,
                              }),
                        colorVariant: 'tertiary',
                        disabled: inquiryBusy || applying || application.uncertain,
                      },
                    ]}
                  />
                </div>
              </div>
            </>
          ) : (
            <MarketContentPanel variant="empty" className="min-h-[320px]">
              <MarketEmptyPanel title="No listing selected" />
            </MarketContentPanel>
          )}
        </section>
      </div>
    </div>
  );

  return (
    <>
      <div
        className="@container/job-search flex min-h-0 min-w-0 flex-1 flex-col"
        data-cy="provider-job-search"
      >
        {/* MCV-1640 mirrors the established B2B search hierarchy while keeping
          the regular-job data and negotiation flows independent. The filters
          panel stays visible above the workspace during loading and errors --
          only the workspace area itself is replaced. */}
        <div className="mb-4">
          <ProviderJobSearchFilters
            onSearch={handleFilterSearch}
            onClearAll={handleClearAll}
            disabled={loading}
            initialValues={initialFilters}
          />
        </div>
        {workspace}
      </div>
      {application.uncertain && !application.open && application.source && (
        <div className="m-4 space-y-2 rounded-md border border-grey-900 p-3">
          <p role="status">
            The application for {application.source.jobTitle} has an unconfirmed outcome.
          </p>
          <MarketActionRow
            actions={[
              {
                label: 'Review original application',
                onClick: () => {
                  if (application.source) application.openApplication(application.source);
                },
              },
            ]}
          />
        </div>
      )}
      <ModalDialog
        open={application.open}
        title="Apply for this job"
        onClose={application.close}
        closeDisabled={applying}
        xButton
      >
        <div className="space-y-4 text-[1rem] leading-6 text-content-primary">
          <p className="font-semibold [overflow-wrap:anywhere]">{application.source?.jobTitle}</p>
          <p>
            Submit your application to express interest in this job. You can negotiate the terms
            after applying.
          </p>
          <p className="text-content-secondary">
            Applying preserves your identity masking. Identities are shared only through the
            unmasking process.
          </p>
          <p role="status" aria-live="polite" className="min-h-6 text-error">
            {application.error}
          </p>
          <MarketActionRow
            size="md"
            actions={[
              {
                label: 'Cancel',
                onClick: application.close,
                disabled: applying,
                colorVariant: 'tertiary',
              },
              {
                label: applying
                  ? 'Submitting Application…'
                  : application.uncertain
                    ? 'Retry original application'
                    : 'Submit Application',
                onClick: application.uncertain ? application.retry : application.submit,
                disabled: applying,
              },
            ]}
          />
          {application.uncertain && !applying && (
            <>
              <p>
                Discarding the local retry does not undo an application that may already have been
                submitted. Check your opportunities before applying again.
              </p>
              <MarketActionRow
                actions={[
                  { label: 'Refresh application outcome', onClick: application.refresh },
                  {
                    label: 'Discard local application retry',
                    onClick: application.discardRetry,
                    colorVariant: 'tertiary',
                  },
                ]}
              />
            </>
          )}
        </div>
      </ModalDialog>
      <ResponseNegotiationModal
        open={negotiationResponseId != null}
        responseId={negotiationResponseId}
        responseType="job"
        viewerRole="PROVIDER"
        startDraftIfEmpty={negotiationStartDraftIfEmpty}
        onDraftSubmitted={handleDraftSubmitted}
        onClose={handleNegotiationClose}
      />
    </>
  );
};
